import React from 'react';
import { Box } from '@material-ui/core';
import { Typography as CommonsTypography, SettingsToggle } from '@orijinworks/frontend-commons';
import {
  permissionGroupBlock,
  permissionToggleRow,
  permissionChildToggleRow,
  permissionToggleTextColumn,
  permissionToggleLabel,
  permissionToggleDescription,
  permissionStatusChipOn,
  permissionStatusChipOff,
} from '../../CreateStaff.styles';
import {
  areAllGranted,
  buildParentToggleChanges,
  buildToggleChanges,
} from '../../utils/staffPermissionSections';

/** Trailing control: an editable toggle, or a read-only ON/OFF status chip. */
function PermissionRowControl({ editable, checked, onChange }) {
  if (editable) {
    return <SettingsToggle checked={checked} onChange={onChange} />;
  }
  return (
    <Box
      component="span"
      style={checked ? permissionStatusChipOn : permissionStatusChipOff}
      aria-label={checked ? 'On' : 'Off'}
    >
      {checked ? 'ON' : 'OFF'}
    </Box>
  );
}

/** One catalog row: title + optional supporting copy + trailing control. */
function PermissionToggleRow({ title, description, checked, editable, onChange, rowStyle }) {
  return (
    <Box style={rowStyle} component="div">
      <Box style={permissionToggleTextColumn}>
        <CommonsTypography variant="body1" component="div" sx={permissionToggleLabel}>
          {title}
        </CommonsTypography>
        {description ? (
          <CommonsTypography variant="body2" component="div" sx={permissionToggleDescription}>
            {description}
          </CommonsTypography>
        ) : null}
      </Box>
      <PermissionRowControl editable={editable} checked={checked} onChange={onChange} />
    </Box>
  );
}

function applyToggleChanges(changes, onToggle) {
  changes.forEach(({ permissionId, enabled }) => onToggle(permissionId, enabled));
}

/** Flat 1:1 rows (e.g. Feeds), one toggle per catalog permission. */
function FlatPermissionRows({ items, toggleState, editable, onToggle }) {
  return items.map((item) => (
    <PermissionToggleRow
      key={item.permissionId}
      title={item.permissionDisplayName || item.permissionName}
      description={item?.description ?? ''}
      checked={Boolean(toggleState[item.permissionId])}
      editable={editable}
      onChange={(next) => onToggle(item.permissionId, next)}
      rowStyle={permissionToggleRow}
    />
  ));
}

/** Parent bundle + conditional child bundles (children render only when parent is ON). */
function ParentChildPermissionRows({ parentChild, toggleState, editable, onToggle }) {
  const parentChecked = areAllGranted(parentChild.parent.permissionIds, toggleState);
  const handleParentChange = (next) =>
    applyToggleChanges(buildParentToggleChanges(parentChild, next), onToggle);

  return (
    <>
      <PermissionToggleRow
        key={parentChild.parent.key}
        title={parentChild.parent.label}
        description={parentChild.parent.description}
        checked={parentChecked}
        editable={editable}
        onChange={handleParentChange}
        rowStyle={permissionToggleRow}
      />
      {parentChecked
        ? parentChild.children.map((child) => (
            <PermissionToggleRow
              key={child.key}
              title={child.label}
              description={child.description}
              checked={areAllGranted(child.permissionIds, toggleState)}
              editable={editable}
              onChange={(next) =>
                applyToggleChanges(buildToggleChanges(child.permissionIds, next), onToggle)
              }
              rowStyle={permissionChildToggleRow}
            />
          ))
        : null}
    </>
  );
}

/**
 * Renders the staff permission catalog as one block per section (header from `groupDisplayName`).
 * Sections without a parent/child bundle render flat 1:1 toggles; bundle sections render a
 * parent toggle + conditional child toggles. `editable` switches between edit toggles and
 * read-only ON/OFF chips.
 */
export default function PermissionCatalogSections({
  sections,
  toggleState,
  editable = true,
  onToggle,
  sectionLabelSx,
  headingComponent = 'h2',
}) {
  return sections.map((section) => (
    <Box key={section.groupName} sx={permissionGroupBlock}>
      <CommonsTypography variant="overline" component={headingComponent} sx={sectionLabelSx}>
        {section.groupDisplayName}
      </CommonsTypography>
      <Box component="div" width="100%">
        {section.parentChild ? (
          <ParentChildPermissionRows
            parentChild={section.parentChild}
            toggleState={toggleState}
            editable={editable}
            onToggle={onToggle}
          />
        ) : (
          <FlatPermissionRows
            items={section.items}
            toggleState={toggleState}
            editable={editable}
            onToggle={onToggle}
          />
        )}
      </Box>
    </Box>
  ));
}
